import { useState, useEffect } from 'react'

export type SidebarPosition = 'docked' | 'floating' | 'overlay' | 'bottom-sheet'

export interface UseSidebarReturn {
  collapsed: boolean
  position: SidebarPosition
  toggle: () => void
  collapse: () => void
  expand: () => void
  isBottomSheet: boolean
}

/**
 * Sidebar state management hook
 *
 * Automatically determines sidebar position based on screen size:
 * - Desktop (>1024px): Docked on right
 * - Tablet (768-1024px): Floating panel
 * - Mobile (<768px): Full-screen overlay
 */
export function useSidebar(): UseSidebarReturn {
  const [collapsed, setCollapsed] = useState(false)
  const [position, setPosition] = useState<SidebarPosition>('docked')

  // Update position based on window size
  useEffect(() => {
    const updatePosition = () => {
      const width = window.innerWidth

      if (width > 1024) {
        setPosition('docked')
        setCollapsed(false) // Always expanded on desktop
      } else if (width > 768) {
        setPosition('floating')
        setCollapsed(false) // Default expanded on tablet
      } else {
        setPosition('bottom-sheet') // Use bottom sheet on mobile
        setCollapsed(true) // Default collapsed (2 lines visible)
      }
    }

    // Initial check
    updatePosition()

    // Listen for resize
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
    }
  }, [])

  const toggle = () => {
    setCollapsed(prev => {
      console.log('[useSidebar] Toggling sidebar:', prev, '->', !prev)
      return !prev
    })
  }

  const collapse = () => {
    console.log('[useSidebar] Collapsing sidebar')
    setCollapsed(true)
  }

  const expand = () => {
    console.log('[useSidebar] Expanding sidebar')
    setCollapsed(false)
  }

  const isBottomSheet = position === 'bottom-sheet'

  return {
    collapsed,
    position,
    toggle,
    collapse,
    expand,
    isBottomSheet
  }
}
